import React, { useMemo } from 'react';
import Select, { createFilter } from 'react-select';
import { VirtualizedMenuList } from './VirtualizedMenuList';
import { EncompassField } from 'src/services/encompass/getEncompassFieldSchema';
import { DocumentType } from 'src/services/documents/types';
import {
  LosFieldValueSource,
  UpdateLosFieldConfig,
} from 'src/services/loans/types';
import { cn } from 'src/utils/cn';

type SelectOption = { value: string; label: string };

const DEFAULT_CONFIG: UpdateLosFieldConfig = {
  enabled: false,
  target: { kind: 'field', encompassFieldId: '' },
};

type ValueSourceType = LosFieldValueSource['type'];

const VALUE_SOURCE_OPTIONS: { value: ValueSourceType; label: string }[] = [
  { value: 'static', label: 'Static value' },
  { value: 'expression', label: 'Expression' },
  { value: 'docField', label: 'Document field' },
  { value: 'ruleResult', label: 'Rule result' },
];

/** Shared input styling so native controls match across the rule builder. */
const inputClass =
  'block w-full rounded border border-gray-300 px-2 py-1.5 text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-xs';
const selectClass =
  'block w-full rounded border border-gray-300 py-1.5 pl-2 pr-6 text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-xs';

/** Faster filter for large option sets — skips per-keystroke accent stripping. */
const fastFilter = createFilter({ ignoreAccents: false });

/** Keep each react-select option on a single line so virtualized rows can't
 *  wrap past their fixed height. */
const clipOption = <T,>(base: T) => ({
  ...base,
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
});

/** Small uppercase section heading used to group the nested controls. */
function SectionLabel({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {children}
    </span>
  );
}

/** Searchable dropdown whose options are always sorted ascending by label. */
function SearchableSelect({
  ariaLabel,
  inputId,
  value,
  options,
  onChange,
  placeholder,
  isDisabled,
}: Readonly<{
  ariaLabel: string;
  inputId: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder: string;
  isDisabled?: boolean;
}>) {
  const sortedOptions = useMemo(() => {
    const seen = new Set<string>();
    const unique = options.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
    return unique.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  }, [options]);

  return (
    <Select<SelectOption>
      aria-label={ariaLabel}
      inputId={inputId}
      isDisabled={isDisabled}
      isClearable
      options={sortedOptions}
      value={sortedOptions.find((option) => option.value === value) ?? null}
      onChange={(option) => onChange(option?.value ?? '')}
      placeholder={placeholder}
      className="text-xs"
      classNamePrefix="los-value-select"
      components={{ MenuList: VirtualizedMenuList }}
      filterOption={fastFilter}
      menuPortalTarget={typeof document === 'undefined' ? null : document.body}
      styles={{
        option: clipOption,
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
    />
  );
}

function ValueSourceEditor({
  idPrefix,
  label,
  accent,
  value,
  onChange,
  selectedField,
  docTypeOptions,
  fieldsByDocType,
}: Readonly<{
  idPrefix: string;
  label: string;
  accent: 'pass' | 'fail';
  value: LosFieldValueSource | undefined;
  onChange: (next: LosFieldValueSource | undefined) => void;
  selectedField: EncompassField | undefined;
  docTypeOptions: SelectOption[];
  fieldsByDocType: Record<string, SelectOption[]>;
}>) {
  const enabled = Boolean(value);
  const type: ValueSourceType = value?.type ?? 'static';

  const handleTypeChange = (nextType: ValueSourceType) => {
    if (nextType === 'static') onChange({ type: 'static', value: '' });
    else if (nextType === 'expression')
      onChange({ type: 'expression', expression: '' });
    else if (nextType === 'ruleResult') onChange({ type: 'ruleResult' });
    else onChange({ type: 'docField', documentType: '', field: '' });
  };

  const checkboxOptions = selectedField?.options ?? [];

  const enabledAccentClass =
    accent === 'pass'
      ? 'border-green-200 bg-green-50/40'
      : 'border-red-200 bg-red-50/40';

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 rounded border p-2 transition-colors',
        enabled ? enabledAccentClass : 'border-gray-200',
      )}
    >
      <div className="flex items-center justify-between">
        <label
          htmlFor={`${idPrefix}-enable`}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-700"
        >
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              accent === 'pass' ? 'bg-green-500' : 'bg-red-500',
            )}
          />
          {label}
        </label>
        <input
          id={`${idPrefix}-enable`}
          type="checkbox"
          checked={enabled}
          aria-label={`Write to LOS ${label.toLowerCase()}`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange(
              event.currentTarget.checked
                ? { type: 'static', value: '' }
                : undefined,
            )
          }
          className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
        />
      </div>

      {enabled && (
        <div className="flex flex-col gap-1.5">
          <select
            aria-label={`${label} value source`}
            value={type}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              handleTypeChange(event.currentTarget.value as ValueSourceType)
            }
            className={selectClass}
          >
            {VALUE_SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {value?.type === 'static' &&
            (checkboxOptions.length > 0 ? (
              <SearchableSelect
                ariaLabel={`${label} static value`}
                inputId={`${idPrefix}-static-value`}
                value={value.value}
                options={checkboxOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.text || opt.value,
                }))}
                onChange={(next) => onChange({ type: 'static', value: next })}
                placeholder="Select…"
              />
            ) : (
              <input
                type="text"
                aria-label={`${label} static value`}
                value={value.value}
                placeholder="Value to write"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onChange({ type: 'static', value: event.currentTarget.value })
                }
                className={inputClass}
              />
            ))}

          {value?.type === 'expression' && (
            <input
              type="text"
              aria-label={`${label} expression`}
              value={value.expression}
              placeholder="e.g. [W2.EmployerName]"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({
                  type: 'expression',
                  expression: event.currentTarget.value,
                })
              }
              className={inputClass}
            />
          )}

          {value?.type === 'docField' && (
            <div className="grid grid-cols-2 gap-1.5">
              <SearchableSelect
                ariaLabel={`${label} document type`}
                inputId={`${idPrefix}-doc-type`}
                value={value.documentType}
                options={docTypeOptions}
                onChange={(next) =>
                  onChange({
                    type: 'docField',
                    documentType: next,
                    field: '',
                  })
                }
                placeholder="Document type…"
              />
              <SearchableSelect
                ariaLabel={`${label} document field`}
                inputId={`${idPrefix}-doc-field`}
                value={value.field}
                isDisabled={!value.documentType}
                options={fieldsByDocType[value.documentType] ?? []}
                onChange={(next) =>
                  onChange({
                    type: 'docField',
                    documentType: value.documentType,
                    field: next,
                  })
                }
                placeholder="Field…"
              />
            </div>
          )}

          {value?.type === 'ruleResult' && (
            <p className="text-[10px] text-gray-500">
              Writes the rule&apos;s evaluated result to this field.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export interface UpdateLosFieldEditorProps {
  /** Current config from the rule definition (`definition.updateLosField`). */
  config: UpdateLosFieldConfig | undefined;
  /** Persist the next config (or `undefined` to clear it). */
  onChange: (next: UpdateLosFieldConfig | undefined) => void;
  /** Encompass field schema (writable + read-only; filtered here). */
  encompassFields: EncompassField[];
  isFieldsLoading: boolean;
  isFieldsError: boolean;
  /** Tenant document types, used for the "Document field" value source. */
  documentTypes: DocumentType[];
}

/**
 * Presentational "Update LOS Field" rule-action editor (MVP-2995).
 *
 * Pure/props-driven so it can be hosted by both the loan-level rule builder
 * (store-bound wrapper, see UpdateLosFieldAction) and the admin checklist
 * management rule builder (local draft state, see RuleManagement).
 */
export function UpdateLosFieldEditor({
  config,
  onChange,
  encompassFields,
  isFieldsLoading,
  isFieldsError,
  documentTypes,
}: Readonly<UpdateLosFieldEditorProps>) {
  const enabled = config?.enabled ?? false;

  const writableFields = useMemo(() => {
    const seen = new Set<string>();
    return encompassFields.filter((f) => {
      if (f.readOnly || seen.has(f.id)) return false;
      seen.add(f.id);
      return true;
    });
  }, [encompassFields]);

  const fieldOptions = useMemo<SelectOption[]>(
    () =>
      writableFields.map((f) => ({
        value: f.id,
        label: f.description ? `${f.id} — ${f.description}` : f.id,
      })),
    [writableFields],
  );

  const selectedFieldId =
    config?.target.kind === 'field' ? config.target.encompassFieldId : '';

  const selectedField = useMemo(
    () => writableFields.find((f) => f.id === selectedFieldId),
    [writableFields, selectedFieldId],
  );

  const docTypeOptions = useMemo<SelectOption[]>(
    () =>
      documentTypes.map((dt) => ({
        value: dt.displayName,
        label: dt.displayName,
      })),
    [documentTypes],
  );

  const fieldsByDocType = useMemo<Record<string, SelectOption[]>>(() => {
    const map: Record<string, SelectOption[]> = {};
    documentTypes.forEach((dt) => {
      map[dt.displayName] = dt.fields.map((field) => ({
        value: field.displayName,
        label: field.displayName,
      }));
    });
    return map;
  }, [documentTypes]);

  const patchConfig = (partial: Partial<UpdateLosFieldConfig>) => {
    const base = config ?? DEFAULT_CONFIG;
    onChange({ ...base, ...partial });
  };

  const toggleEnabled = () => {
    if (enabled) {
      onChange({ ...(config ?? DEFAULT_CONFIG), enabled: false });
    } else {
      onChange({ ...DEFAULT_CONFIG, ...config, enabled: true });
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded border p-2.5',
        enabled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200',
      )}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-700">
            Update LOS Field
          </span>
          <span className="text-[10px] text-gray-500">
            Write rule outcome to Encompass
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Enable update LOS field"
          className={cn(
            'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
            enabled ? 'bg-blue-600' : 'bg-gray-200',
          )}
          onClick={toggleEnabled}
        >
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              enabled ? 'translate-x-3' : 'translate-x-0',
            )}
          />
        </button>
      </div>

      {enabled && config && (
        <div className="flex flex-col gap-3 border-t border-blue-100 pt-2.5">
          {/* Target */}
          <div className="flex flex-col gap-1.5">
            <SectionLabel>Target</SectionLabel>
            <div className="grid grid-cols-2 gap-1.5">
              <select
                aria-label="LOS target type"
                value={config.target.kind}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  patchConfig({
                    target:
                      event.currentTarget.value === 'milestone'
                        ? { kind: 'milestone', milestone: '' }
                        : { kind: 'field', encompassFieldId: '' },
                  })
                }
                className={selectClass}
              >
                <option value="field">Encompass field</option>
                <option value="milestone">Milestone</option>
              </select>

              {config.target.kind === 'milestone' ? (
                <input
                  type="text"
                  aria-label="Milestone name"
                  value={config.target.milestone}
                  placeholder="Milestone name"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    patchConfig({
                      target: {
                        kind: 'milestone',
                        milestone: event.currentTarget.value,
                      },
                    })
                  }
                  className={inputClass}
                />
              ) : (
                <Select<SelectOption>
                  aria-label="Encompass field"
                  inputId="los-encompass-field"
                  isLoading={isFieldsLoading}
                  options={fieldOptions}
                  value={
                    fieldOptions.find((o) => o.value === selectedFieldId) ??
                    null
                  }
                  onChange={(option) =>
                    patchConfig({
                      target: {
                        kind: 'field',
                        encompassFieldId: option?.value ?? '',
                      },
                    })
                  }
                  placeholder="Search field…"
                  className="text-xs"
                  classNamePrefix="los-field-select"
                  components={{ MenuList: VirtualizedMenuList }}
                  filterOption={fastFilter}
                  styles={{ option: clipOption }}
                />
              )}
            </div>

            {isFieldsError && (
              <p role="alert" className="text-[10px] text-red-600">
                Failed to load Encompass fields.
              </p>
            )}
          </div>

          {/* Per-outcome value sources */}
          <div className="flex flex-col gap-1.5">
            <SectionLabel>Values to write</SectionLabel>
            <ValueSourceEditor
              idPrefix="los-on-true"
              label="On Pass"
              accent="pass"
              value={config.onTrue}
              onChange={(next) => patchConfig({ onTrue: next })}
              selectedField={selectedField}
              docTypeOptions={docTypeOptions}
              fieldsByDocType={fieldsByDocType}
            />
            <ValueSourceEditor
              idPrefix="los-on-false"
              label="On Fail"
              accent="fail"
              value={config.onFalse}
              onChange={(next) => patchConfig({ onFalse: next })}
              selectedField={selectedField}
              docTypeOptions={docTypeOptions}
              fieldsByDocType={fieldsByDocType}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateLosFieldEditor;
